import type {DeepKeys} from '@linx-security/table';
import type {FilterOperators} from 'mongodb';

import type {Path} from './deep-keys';

type AccumulatorExpression = {
  $sum: 1;
};

type AggregationObjectHelper<TData extends Record<string, any>> = {
  $group: <TAccumulator extends keyof AccumulatorExpression>(group: {
    _id: Path<TData>;
    count: {[key in TAccumulator]: AccumulatorExpression[TAccumulator]};
  }) => AggregationObjectHelper<TData>;
  $match: (criteria: {
    [key in DeepKeys<TData>]?: FilterOperators<TData[key]>;
  }) => AggregationObjectHelper<TData>;
  /** If used with $group Accepts only '_id' and 'count' fields in the project object. Otherwise accepts any key and the value is a key of the type */
  $project: (project: Record<string, Path<TData> | '_id' | 'count'>) => AggregationObjectHelper<TData>;
  asString: () => string;
};

/**
 * Creates an aggregation helper for building MongoDB aggregation pipelines.
 *
 * @example
 * const helper = createAggregationHelper<{ field1: string; field2: string }>();
 *
 * helper
 *   .$group({ _id: 'field1', count: { $sum: 1 } })
 *   .$project({ field1Alias: 'field1', countAlias: 'count' });
 *
 * console.log(helper.asString());
 *
 * @example
 * const helper = createAggregationHelper<HumanApiServiceModel>();
 *
 * helper
 *   .$project({
 *     funny_name: 'display_name',
 *     first_data_source_slug: 'data_sources.0.slug',
 *   });
 *
 * console.log(helper.asString());
 */
export const createAggregationHelper = <TData extends Record<string, any>>(): AggregationObjectHelper<TData> => {
  const stages: Array<any> = [];
  const $group: Record<string, any> = {};
  const $project: Record<string, any> = {};

  const result: AggregationObjectHelper<TData> = {
    $group: group => {
      // Ensure the _id field is included and is a key from the data source
      if (!group._id) {
        throw new Error(`_id field is mandatory in $group stage`);
      }

      // Set the _id field for the group stage
      $group._id = `$${group._id}`;

      // Add other fields to the group stage with support for accumulators like $sum
      Object.keys(group).forEach(key => {
        if (key !== `_id`) {
          // @ts-expect-error: TS doesn't support dynamic keys in types
          const expression = group[key];
          const accumulator = Object.keys(expression)[0];
          if (accumulator === `$sum`) {
            $group[key] = {$sum: expression[accumulator]};
          } else {
            throw new Error(`Currently only $sum is supported as an accumulator`);
          }
        }
      });

      stages.push({$group});
      return result;
    },
    $match: criteria => {
      stages.push({$match: criteria});
      return result;
    },
    $project: project => {
      // Add fields to the project stage
      Object.keys(project).forEach(key => {
        $project[key] = project[key].toString().startsWith(`$`) ? project[key] : `$${project[key]}`;
      });

      stages.push({$project});
      return result;
    },
    asString: () => {
      // Return the aggregation stages as a JSON string
      return JSON.stringify(stages, null, 2);
    },
  };

  return result;
};
